import React, { Fragment, useCallback, useMemo, useState } from "react";

import { useStoredDataDispatch } from "@/store";
import useTokenDistributionBalance from "@/hooks/piggy/useTokenDistributionBalance";
import usePiggyHarvestStart from "@/hooks/piggy/usePiggyHarvestStart";

import PiggyToken from "@/components/Piggy/Token";
import Web3PiggyDepositTx from "@/components/Web3/Piggy/Deposit/Tx";
import UiLabelChainScanLink from "@/components/Ui/Label/ChainScanLink";
import Web3CommonSwitchNetwork from "@/components/Web3/Common/SwitchNetwork";

import { SupAssetLabel, SupButton, SupLoader, SupText } from "@repo/core-ui";
import { SupSuperformMorph } from "@repo/core-ui";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";

import { twMerge } from "tailwind-merge";
import { formatEther } from "viem";

interface Web3PiggyHarvestProps {
  headerClassName?: string;
}

export const TARGET_CHAIN_ID = 8453; // Base

export default function Web3PiggyHarvest({ headerClassName }: Web3PiggyHarvestProps) {
  const storedDispatch = useStoredDataDispatch();
  const { data, isLoading, mutate } = useTokenDistributionBalance(true);

  const { network } = useDynamicContext();

  const [txHash, setTxHash] = useState<string | undefined>();

  const requireSwitchChain = useMemo(() => {
    return network ? parseInt(`${network}`, 10) !== TARGET_CHAIN_ID : false;
  }, [network]);

  const hasValidAmount = useMemo(() => {
    if (!data || isLoading) {
      return false;
    }
    return data.availableAmount > 0n;
  }, [data, isLoading]);

  const { data: apiData, error: apiError, isLoading: apiIsLoading } = usePiggyHarvestStart(hasValidAmount, true);

  const isCombinedLoading = useMemo(() => isLoading || apiIsLoading, [apiIsLoading, isLoading]);

  const handleTxHash = useCallback(
    (hash: string) => {
      setTxHash(hash);
      setTimeout(() => mutate(), 10000);
    },
    [mutate]
  );

  const availableBalance = useMemo(() => {
    if (isLoading || !data?.availableAmount) {
      return "0";
    }
    return formatEther(data.availableAmount);
  }, [data?.availableAmount, isLoading]);

  const handleDone = useCallback(() => {
    storedDispatch({ type: "closeOverlay" });
  }, [storedDispatch]);

  return (
    <Fragment>
      <header className={twMerge("border-bottom-[.5px] fixed top-0 z-50 flex w-[370px] items-center justify-between space-x-2 border-neutral-400 py-2.5", headerClassName)}>
        <h2 className="txt-header-dgt my-0 text-[16px] leading-10">HARVEST</h2>
        <SupSuperformMorph className="h-6 w-6 fill-neutral-900" />
      </header>
      {txHash ? (
        <Fragment>
          {/* Finished step */}
          {txHash && TARGET_CHAIN_ID && (
            <div className="-ml-2 mt-6">
              <UiLabelChainScanLink urlLabel="Explorer" prefixLabel="View on" address={txHash} chain={TARGET_CHAIN_ID} />
            </div>
          )}
          <div className="mt-auto space-y-2">
            <SupButton fluid onClick={handleDone} className="bg-primary-800 hover:opacity-80" color="primary">
              Done
            </SupButton>
          </div>
        </Fragment>
      ) : (
        <Fragment>
          <div className="flex h-10 w-full items-center justify-between border-b-[.5px] border-neutral-200">
            <SupText variant="subtitle1" className="text-[14px] leading-3 tracking-[1px]">
              $PIGGY
            </SupText>
            <SupAssetLabel
              className="flex w-fit items-center gap-2"
              label={
                <SupText variant="subtitle1" className="text-[14px] leading-4">
                  {parseFloat(availableBalance).toFixed(6)}
                </SupText>
              }
            >
              <PiggyToken isSmall />
            </SupAssetLabel>
          </div>
          <div className="mt-auto space-y-2">
            <div className="mt-auto flex w-full flex-col items-stretch justify-between space-y-4">
              {isCombinedLoading ? (
                <SupLoader />
              ) : (
                hasValidAmount &&
                (requireSwitchChain ? (
                  <Web3CommonSwitchNetwork targetChainId={parseInt(`${TARGET_CHAIN_ID}`)} />
                ) : (
                  <Web3PiggyDepositTx
                    action="harvesting"
                    name="harvest"
                    apiData={apiData}
                    apiError={apiError}
                    apiIsLoading={apiIsLoading}
                    handleTxHash={handleTxHash}
                    chainId={TARGET_CHAIN_ID}
                  />
                ))
              )}
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
}
